import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { auth, db } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Icon from '../../../../components/Icon';
import Modal from '../../../../components/Modal';
import Table, { Column } from '../../../../components/Table';
import { 
    FiPlus, FiSearch, FiFilter, FiX, FiCheckCircle, 
    FiXCircle, FiRefreshCw, FiFileText, FiDownload, 
    FiEye, FiEdit2, FiChevronDown, FiTrash2
} from 'react-icons/fi';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { designationIsMisIncharge } from '../../../../utils/designationRole';
import { 
    subscribeToLeaseRenewalSurveys, 
    fetchLeaseRenewalSurveys, 
    deleteLeaseRenewalSurvey,
    type LeaseRenewalSurvey } from '../service/leaseRenewalSurveyService';
import AddLeaseRenewalSurveyModal from '../components/AddLeaseRenewalSurveyModal';
import LeaseRenewalSurveyDetailsModal from '../components/LeaseRenewalSurveyDetailsModal';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function LeaseRenewalSurveys() {
    const [loading, setLoading] = useState(false);
    const [surveys, setSurveys] = useState<LeaseRenewalSurvey[]>([]);
    const [filtered, setFiltered] = useState<LeaseRenewalSurvey[]>([]);
    const [searchVal, setSearchVal] = useState('');
    
    // Auth & Designation
    const psoProfile = useAppSelector((s) => s.user.pso);
    const bankProfile = useAppSelector((s) => s.user.bank);
    const currentUser = useAppSelector((s) => s.user.currentUser);
    const isMISIncharge = useMemo(() => designationIsMisIncharge(psoProfile?.designation), [psoProfile]);
    const isBankUser = useMemo(() => !psoProfile && !!bankProfile, [psoProfile, bankProfile]);

    // Modal Control States
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsModalMode, setDetailsModalMode] = useState<'view' | 'submission'>('view');
    const [selectedSurvey, setSelectedSurvey] = useState<LeaseRenewalSurvey | null>(null);
    const [surveyToDelete, setSurveyToDelete] = useState<LeaseRenewalSurvey | null>(null);
    const [showFilterModal, setShowFilterModal] = useState(false);

    // Right-Click Context Menu State
    const [contextMenu, setContextMenu] = useState<{
        x: number;
        y: number;
        visible: boolean;
        survey: LeaseRenewalSurvey | null;
    }>({ x: 0, y: 0, visible: false, survey: null });

    // Toast notifications
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

    // Export progress state
    const [exportProgress, setExportProgress] = useState({
        active: false,
        format: 'excel' as 'excel' | 'pdf',
        percent: 0,
        message: '',
        processedRows: 0,
        totalRows: 0,
        done: false,
    });
    const cancelExportRef = useRef(false);

    // Export dropdown state and ref
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    // Search and Filter parameters
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [psoFilter, setPsoFilter] = useState<string>('all');
    const [requestDateStart, setRequestDateStart] = useState<string>('');
    const [requestDateEnd, setRequestDateEnd] = useState<string>('');

    const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
        setToast({ message, type });
    };

    // Click outside handler for export dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
                setExportMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load initial list via subscription
    useEffect(() => {
        setLoading(true);
        const unsubscribe = subscribeToLeaseRenewalSurveys((data) => {
            setSurveys(data);
            setFiltered(data);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [psoProfile]);

    // Apply Client filters
    const applyFilters = () => {
        let temp = [...surveys];

        // Search contains: case-insensitive match on Survey No or Branch Code
        const q = searchVal.trim().toLowerCase();
        if (q) {
            temp = temp.filter(s => 
                (s.surveyId || '').toLowerCase().includes(q) ||
                (s.branchCode || '').toLowerCase().includes(q)
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            temp = temp.filter(s => s.status === statusFilter);
        }

        // PSO Name filter
        if (psoFilter !== 'all') {
            temp = temp.filter(s => s.assignedToName === psoFilter);
        }

        // Date ranges (Request Date)
        if (requestDateStart) {
            temp = temp.filter(s => s.requestDate >= requestDateStart);
        }
        if (requestDateEnd) {
            temp = temp.filter(s => s.requestDate <= requestDateEnd);
        }

        setFiltered(temp);
        setShowFilterModal(false);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            applyFilters();
        }
    };

    const clearFilters = () => {
        setStatusFilter('all');
        setPsoFilter('all');
        setRequestDateStart('');
        setRequestDateEnd('');
        setSearchVal('');
        setFiltered(surveys);
        setShowFilterModal(false);
    };

    const hasActiveFilters = useMemo(() => {
        return statusFilter !== 'all' || psoFilter !== 'all' || !!requestDateStart || !!requestDateEnd;
    }, [statusFilter, psoFilter, requestDateStart, requestDateEnd]);

    // Auto get list of all assigned PSO names for filtering dropdown
    const psoNamesList = useMemo(() => {
        const set = new Set<string>();
        surveys.forEach(s => {
            if (s.assignedToName) set.add(s.assignedToName);
        });
        return Array.from(set).sort();
    }, [surveys]);

    // Premium Statistics calculation
    const statistics = useMemo(() => {
        let assigned = 0;
        let submitted = 0;
        let infraOn = 0;
        let infraOff = 0;

        let rcc = 0;
        let isolated = 0;
        let weak = 0;
        let fuel = 0;
        let extensions = 0;

        filtered.forEach(s => {
            if (s.status === 'Submitted') submitted++;
            else assigned++;

            if (s.hasInfrastructure) infraOn++;
            else infraOff++;

            if (s.rccStructured) rcc++;
            if (s.isolatedLocation) isolated++;
            if (s.weakSecurity) weak++;
            if (s.fuelStationNearby) fuel++;
            if (s.suitableForExtension) extensions++;
        });

        return {
            assigned, submitted, infraOn, infraOff,
            rcc, isolated, weak, fuel, extensions
        };
    }, [filtered]);

    const handleDeleteSurvey = async () => {
        if (!surveyToDelete) return;
        const survey = surveyToDelete;
        const resolvedBankId = sessionStorage.getItem('bankDocId') || bankProfile?.bankDocId || survey.bankId;
        if (!resolvedBankId) {
            showToast('Bank ID not found. Cannot delete survey.', 'error');
            return;
        }

        try {
            setLoading(true);
            await deleteLeaseRenewalSurvey(resolvedBankId, survey.id);
            showToast('Survey request deleted successfully.', 'success');
            setSurveyToDelete(null);
        } catch (error) {
            console.error('Error deleting survey:', error);
            showToast('Failed to delete survey request.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Right Click handlers
    const handleRowContextMenu = (e: React.MouseEvent<HTMLTableRowElement>, row: any) => {
        e.preventDefault();
        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            visible: true,
            survey: row as LeaseRenewalSurvey,
        });
    };

    const closeContextMenu = () => {
        setContextMenu(prev => ({ ...prev, visible: false }));
    };

    useEffect(() => {
        const handleGlobalClick = () => {
            if (contextMenu.visible) closeContextMenu();
        };
        window.addEventListener('click', handleGlobalClick);
        return () => window.removeEventListener('click', handleGlobalClick);
    }, [contextMenu.visible]);

    // Single survey PDF generation
    const triggerSinglePdf = (item: LeaseRenewalSurvey) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const primaryColor: [number, number, number] = [0, 131, 123];
        const accentColor: [number, number, number] = [0, 131, 123];

        // Header Banner
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.rect(0, 0, 210, 26, 'F');

        // Title (Centered)
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text('LEASE AGREEMENT RENEWAL SURVEY REPORT', 105, 16, { align: 'center' });

        const dateStr = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });

        // Metadata grid
        const metadataRows = [
            [
                { content: 'Survey No:', styles: { fontStyle: 'bold' as const } },
                item.surveyId || 'N/A',
                { content: 'Status:', styles: { fontStyle: 'bold' as const } },
                item.status || 'Assigned'
            ],
            [
                { content: 'Branch Code:', styles: { fontStyle: 'bold' as const } },
                item.branchCode || 'N/A',
                { content: 'Branch Type:', styles: { fontStyle: 'bold' as const } },
                item.branchType || 'Branch'
            ],
            [
                { content: 'Request Date:', styles: { fontStyle: 'bold' as const } },
                item.requestDate || 'N/A',
                { content: 'Assigned PSO:', styles: { fontStyle: 'bold' as const } },
                item.assignedToName || 'N/A'
            ],
            [
                { content: 'Survey Date:', styles: { fontStyle: 'bold' as const } },
                item.surveyDate || 'N/A',
                { content: 'Renewal Period:', styles: { fontStyle: 'bold' as const } },
                item.renewalPeriod || 'N/A'
            ]
        ];

        autoTable(doc, {
            startY: 32,
            theme: 'plain',
            body: metadataRows,
            bodyStyles: { fontSize: 8.5, cellPadding: 1.5 },
            columnStyles: {
                0: { cellWidth: 28 },
                1: { cellWidth: 62 },
                2: { cellWidth: 28 },
                3: { cellWidth: 62 }
            }
        });

        // Address Box
        const inchargeText = `Region: ${item.branchLocation || 'N/A'}\nBranch Name: ${item.branchName || 'N/A'}`;
        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 3,
            theme: 'grid',
            head: [['Brand Info', 'Branch Address']],
            body: [[inchargeText, item.addressOfBranch || 'N/A']],
            headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 8.5, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 90 },
                1: { cellWidth: 90 }
            }
        });

        let currentY = (doc as any).lastAutoTable.finalY + 3;

        // Checklist Facilities (Conditional)
        if (item.hasInfrastructure) {
            const facilitiesList: string[] = [];
            const labelsMap: Record<string, string> = {
                ptclLine: 'PTCL Line',
                atm: 'ATM',
                locker: 'Locker',
                exit: 'Emergency Exit',
                vantagePoint: 'Vantage Point',
                parkingStaff: 'Parking Space for staff',
                parkingCit: 'Parking Space for CIT Van',
                generatorSpace: 'Generator Space',
            };
            if (item.infrastructureDetails) {
                Object.entries(item.infrastructureDetails).forEach(([k, v]) => {
                    if (v) facilitiesList.push(labelsMap[k] || k);
                });
            }
            const facilitiesStr = facilitiesList.length > 0 
                ? facilitiesList.join(', ') 
                : 'No special facilities selected.';

            autoTable(doc, {
                startY: currentY,
                theme: 'grid',
                head: [['INFRASTRUCTURE & FACILITIES']],
                body: [[facilitiesStr]],
                headStyles: { fillColor: accentColor, textColor: [255, 255, 255], fontStyle: 'bold' },
                bodyStyles: { fontSize: 8.5, cellPadding: 3 }
            });
            currentY = (doc as any).lastAutoTable.finalY + 3;
        }

        // Assessment Details Checklist
        const yesNo = (v: boolean | undefined) => v ? 'Yes' : 'No';
        const questionRows = [
            ['RCC Structured (Locker / Strong Room)', yesNo(item.rccStructured)],
            ['Isolated Location', yesNo(item.isolatedLocation)],
            ['Weak or Not Suitable from Security Point of View', yesNo(item.weakSecurity)],
            ['Flood Area', yesNo(item.floodArea)],
            ['Public Nuisance Area', yesNo(item.publicNuisance)],
            ['Critical Risk Building in Area', yesNo(item.criticalRiskBuilding)],
            ['Fuel Station Nearby', yesNo(item.fuelStationNearby)],
            ['Suitable for Extension / Renewal', yesNo(item.suitableForExtension)],
        ];

        autoTable(doc, {
            startY: currentY,
            theme: 'grid',
            head: [['Assessment Factor / Parameter Checked', 'Condition Met / Checked']],
            body: questionRows,
            headStyles: { fillColor: accentColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 140 },
                1: { cellWidth: 40, halign: 'center', fontStyle: 'bold' as const }
            },
            bodyStyles: { fontSize: 8.5, cellPadding: 2 }
        });

        // Professional Remarks Table
        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 3,
            theme: 'grid',
            head: [['Remarks']],
            body: [[item.remarks || 'No relocation remarks provided.']],
            headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 8.5, cellPadding: 4 }
        });

        // Footer block
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated By: ${item.assignedToName || 'System'}      |      Date: ${dateStr}`, 15, pageHeight - 9);

        const fileName = `Lease_Renewal_Report_${item.surveyId || 'N/A'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        showToast('Single survey PDF exported successfully.', 'success');
    };

    // Excel Export with responsive progress widget
    const handleExcelExport = async () => {
        if (filtered.length === 0) {
            showToast('No lease surveys to export.', 'warning');
            return;
        }

        cancelExportRef.current = false;
        setExportProgress({
            active: true,
            format: 'excel',
            percent: 0,
            message: 'Starting Excel Export compilation...',
            processedRows: 0,
            totalRows: filtered.length,
            done: false,
        });

        const dataRows: any[] = [];
        const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        try {
            for (let i = 0; i < filtered.length; i++) {
                if (cancelExportRef.current) {
                    showToast('Export cancelled by user.', 'warning');
                    setExportProgress(p => ({ ...p, active: false }));
                    return;
                }

                const s = filtered[i];
                
                // Construct infrastructure text details
                const infraList: string[] = [];
                if (s.hasInfrastructure && s.infrastructureDetails) {
                    Object.entries(s.infrastructureDetails).forEach(([k, v]) => {
                        if (v) infraList.push(k);
                    });
                }
                const infraStr = infraList.join(', ');

                dataRows.push({
                    'Survey No': s.surveyId || 'N/A',
                    'Branch Code': s.branchCode || 'N/A',
                    'Request Date': s.requestDate || 'N/A',
                    'Survey Date': s.surveyDate || 'N/A',
                    'Assigned PSO': s.assignedToName || 'N/A',
                    'Branch Name (Incharge Address)': s.branchName || 'N/A',
                    'PSO Physical Address': s.addressOfBranch || 'N/A',
                    'Branch Type': s.branchType || 'N/A',
                    'Renewal Period': s.renewalPeriod || 'N/A',
                    'Infrastructure Switch': s.hasInfrastructure ? 'Yes' : 'No',
                    'Infrastructure List': infraStr || 'N/A',
                    'RCC Structured Locker': s.rccStructured ? 'Yes' : 'No',
                    'Isolated Location': s.isolatedLocation ? 'Yes' : 'No',
                    'Weak Security Point of View': s.weakSecurity ? 'Yes' : 'No',
                    'Flood Area': s.floodArea ? 'Yes' : 'No',
                    'Public Nuisance': s.publicNuisance ? 'Yes' : 'No',
                    'Critical Risk Building': s.criticalRiskBuilding ? 'Yes' : 'No',
                    'Fuel Station Nearby': s.fuelStationNearby ? 'Yes' : 'No',
                    'Suitable for Extension': s.suitableForExtension ? 'Yes' : 'No',
                    'Remarks': s.remarks || 'No remarks',
                    'Status': s.status || 'Assigned',
                });

                // Simulate processing delay for smooth visual transition
                await sleep(40);
                const percent = ((i + 1) / filtered.length) * 100;
                setExportProgress(prev => ({
                    ...prev,
                    percent,
                    processedRows: i + 1,
                    message: `Compiled Row ${i + 1} of ${filtered.length}`,
                }));
            }

            const ws = XLSX.utils.json_to_sheet(dataRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Lease Renewals');
            XLSX.writeFile(wb, `Lease_Renewal_Surveys_${new Date().toISOString().split('T')[0]}.xlsx`);

            setExportProgress(prev => ({
                ...prev,
                percent: 100,
                message: 'Excel spreadsheet compiled and saved successfully.',
                done: true,
            }));
            showToast('Excel report downloaded successfully.', 'success');
        } catch (err: any) {
            console.error('Excel compile error:', err);
            showToast('Failed to compile Excel spreadsheet.', 'error');
            setExportProgress(p => ({ ...p, active: false }));
        }
    };

    // PDF Summary list export with stats tables
    const handlePdfExport = async () => {
        if (filtered.length === 0) {
            showToast('No lease renewal surveys to export.', 'warning');
            return;
        }

        cancelExportRef.current = false;
        setExportProgress({
            active: true,
            format: 'pdf',
            percent: 0,
            message: 'Preparing PDF compilation system...',
            processedRows: 0,
            totalRows: filtered.length,
            done: false,
        });

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        try {
            const themeColor: [number, number, number] = [0, 131, 123];

            // Header Banner
            doc.setFillColor(themeColor[0], themeColor[1], themeColor[2]);
            doc.rect(0, 0, 210, 26, 'F');

            // Centered Title
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('LEASE AGREEMENT RENEWALS SUMMARY REPORT', 105, 16, { align: 'center' });

            const dateStr = new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });

            // Side-by-side tables for statistics
            const statusData = [
                ['Assigned Requests', statistics.assigned],
                ['Submitted Assessments', statistics.submitted],
                ['Total Scope', filtered.length],
            ];

            const infraData = [
                ['Special Facilities ON', statistics.infraOn],
                ['Special Facilities OFF', statistics.infraOff],
                ['Total Scope', filtered.length],
            ];

            const riskData = [
                ['RCC Strong Room', statistics.rcc],
                ['Isolated Premises', statistics.isolated],
                ['Weak Security Site', statistics.weak],
                ['Fuel Station Proximity', statistics.fuel],
                ['Total Scope', filtered.length],
            ];

            // Render stats table 1
            autoTable(doc, {
                startY: 32,
                margin: { left: 15 },
                tableWidth: 55,
                theme: 'grid',
                head: [['Status Scope', 'Count']],
                body: statusData,
                headStyles: { fillColor: themeColor, textColor: [255, 255, 255], fontStyle: 'bold' },
                bodyStyles: { fontSize: 7.5, cellPadding: 1.5 },
            });

            // Render stats table 2
            autoTable(doc, {
                startY: 32,
                margin: { left: 78 },
                tableWidth: 55,
                theme: 'grid',
                head: [['Facilities Profile', 'Count']],
                body: infraData,
                headStyles: { fillColor: themeColor, textColor: [255, 255, 255], fontStyle: 'bold' },
                bodyStyles: { fontSize: 7.5, cellPadding: 1.5 },
            });

            // Render stats table 3
            autoTable(doc, {
                startY: 32,
                margin: { left: 140 },
                tableWidth: 55,
                theme: 'grid',
                head: [['Risk Factors', 'Count']],
                body: riskData,
                headStyles: { fillColor: themeColor, textColor: [255, 255, 255], fontStyle: 'bold' },
                bodyStyles: { fontSize: 7.5, cellPadding: 1.5 },
            });

            await sleep(100);
            setExportProgress(p => ({ ...p, percent: 35, message: 'Stats compiled. Generating row data...' }));

            const tableBody: any[] = [];
            for (let i = 0; i < filtered.length; i++) {
                if (cancelExportRef.current) {
                    showToast('Export cancelled by user.', 'warning');
                    setExportProgress(p => ({ ...p, active: false }));
                    return;
                }

                const s = filtered[i];
                tableBody.push([
                    s.surveyId || 'N/A',
                    s.branchCode || 'N/A',
                    s.requestDate || 'N/A',
                    s.assignedToName || 'N/A',
                    s.addressOfBranch || s.branchName || 'N/A',
                    s.suitableForExtension ? 'Yes' : 'No',
                    s.status || 'Assigned',
                ]);

                await sleep(20);
                const percent = 35 + ((i + 1) / filtered.length) * 60;
                setExportProgress(prev => ({
                    ...prev,
                    percent,
                    processedRows: i + 1,
                    message: `Building rows: ${i + 1} of ${filtered.length}`,
                }));
            }

            const lastY = Math.max((doc as any).lastAutoTable?.finalY || 95, 95);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10.5);
            doc.setTextColor(51, 65, 85);
            doc.text('LEASE AGREEMENT SURVEY LIST', 15, lastY + 10);

            autoTable(doc, {
                startY: lastY + 14,
                head: [['Survey No', 'Code', 'Req Date', 'PSO Name', 'Physical Address', 'Suitable', 'Status']],
                body: tableBody,
                headStyles: { fillColor: themeColor, textColor: [255, 255, 255], fontStyle: 'bold' },
                bodyStyles: { fontSize: 7.5, cellPadding: 2 },
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { cellWidth: 15 },
                    2: { cellWidth: 20 },
                    3: { cellWidth: 32 },
                    4: { cellWidth: 55 },
                    5: { cellWidth: 16, halign: 'center' },
                    6: { cellWidth: 20 }
                }
            });

            const pageHeight = doc.internal.pageSize.height;
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.setFont('helvetica', 'normal');
            doc.text(`Generated By: System      |      Date: ${dateStr}`, 15, pageHeight - 9);

            doc.save(`Lease_Renewals_Summary_${new Date().toISOString().split('T')[0]}.pdf`);

            setExportProgress(prev => ({
                ...prev,
                percent: 100,
                message: 'PDF compilation completed successfully.',
                done: true,
            }));
            showToast('PDF Summary Report downloaded successfully.', 'success');
        } catch (err: any) {
            console.error('PDF export error:', err);
            showToast('Failed to compile PDF document.', 'error');
            setExportProgress(p => ({ ...p, active: false }));
        }
    };

    const columns: Column<LeaseRenewalSurvey>[] = [
        {
            header: 'Survey No',
            accessor: 'surveyId',
            width: '12%',
            render: (row) => (
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {row.surveyId}
                </span>
            ),
        },
        {
            header: 'Branch Code / Address',
            width: '28%',
            render: (row) => (
                <div className="flex flex-col gap-0.5">
                    <span className="font-bold text-slate-900 dark:text-white">
                        {row.branchCode || '—'}
                    </span>
                    <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                        {row.branchName || '—'}
                    </span>
                </div>
            ),
        },
        {
            header: 'Assigned PSO / Region',
            width: '25%',
            render: (row) => (
                <div className="flex flex-col gap-0.5">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                        {row.assignedToName || '—'}
                    </span>
                    <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                        {row.branchLocation || '—'}
                    </span>
                </div>
            ),
        },
        {
            header: 'Request Date',
            accessor: 'requestDate',
            width: '12%',
        },
        {
            header: 'Survey Date',
            accessor: 'surveyDate',
            width: '12%',
            render: (row) => row.surveyDate || '—',
        },
        {
            header: 'Status',
            accessor: 'status',
            width: '12%',
            render: (row) => {
                const isSubmitted = row.status === 'Submitted';
                return (
                    <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                            isSubmitted
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400'
                                : 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400'
                        }`}
                    >
                        <span
                            className={`h-1.5 w-1.5 rounded-full ${
                                isSubmitted ? 'bg-emerald-500' : 'bg-amber-500'
                            }`}
                        />
                        {row.status}
                    </span>
                );
            },
        },
        {
            header: 'Actions',
            width: '15%',
            align: 'right',
            render: (row) => {
                const showSubmission = row.status === 'Assigned' && row.assignedTo === auth.currentUser?.uid;
                const isSubmitted = row.status === 'Submitted';
                return (
                    <div className="flex items-center justify-end gap-1.5">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSurvey(row);
                                setDetailsModalMode('view');
                                setShowDetailsModal(true);
                            }}
                            className="rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                            title="View Details"
                        >
                            <Icon icon={FiEye} className="h-4 w-4" />
                        </button>

                        {showSubmission && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSurvey(row);
                                    setDetailsModalMode('submission');
                                    setShowDetailsModal(true);
                                }}
                                className="rounded-lg p-1.5 text-amber-600 transition hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/20"
                                title="Submission"
                            >
                                <Icon icon={FiEdit2} className="h-4 w-4" />
                            </button>
                        )}

                        {isSubmitted && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    triggerSinglePdf(row);
                                }}
                                className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                                title="Download PDF"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                            </button>
                        )}

                        {isBankUser && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSurveyToDelete(row);
                                }}
                                className="rounded-lg p-1.5 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                title="Delete Request"
                            >
                                <Icon icon={FiTrash2} className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div className="p-6 space-y-6">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Premium Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 dark:border-slate-800 pb-5">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
                        <Icon icon={FiRefreshCw} className="text-blue-500 animate-spin-slow" />
                        Lease Renewal Survey Management
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Initiate, allocate, and assess branch lease agreement renewals with strict data parameters.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {isMISIncharge && (
                        <Button
                            variant="primary"
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-lg hover:shadow-blue-500/20"
                        >
                            <Icon icon={FiPlus} />
                            Initiate Request
                        </Button>
                    )}
                </div>
            </div>

            {/* Main Action Search and Options Block (Replicated exactly from NewLocationSurveys.tsx) */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex w-full flex-1 items-stretch gap-3 md:pr-3">
                    <div className="min-w-0 flex-1">
                        <TextField
                            type="text"
                            className="!h-11"
                            placeholder="Search by Survey ID or Branch Code (press Enter or click search)..."
                            value={searchVal}
                            onChange={(e) => setSearchVal(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            prefix={<Icon icon={FiSearch} />}
                            suffix={
                                searchVal ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchVal('');
                                            setFiltered(surveys);
                                        }}
                                        className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                        aria-label="Clear search"
                                        title="Clear search"
                                    >
                                        <Icon icon={FiX} className="h-4 w-4" />
                                    </button>
                                ) : null
                            }
                        />
                    </div>
                    <Button onClick={applyFilters} className="!h-11 !py-0 flex shrink-0 items-center gap-2">
                        <Icon icon={FiSearch} className="h-4 w-4" />
                        Search
                    </Button>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-3">
                    {/* Export Dropdown Menu matching NewLocationSurveys */}
                    <div className="relative" ref={exportMenuRef}>
                        <button
                            type="button"
                            onClick={() => setExportMenuOpen((o) => !o)}
                            className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                        >
                            <Icon icon={FiDownload} className="h-4 w-4" />
                            Export
                            <Icon icon={FiChevronDown} className={`h-4 w-4 shrink-0 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {exportMenuOpen && (
                            <div className="absolute right-0 z-40 mt-1 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                    onClick={() => {
                                        setExportMenuOpen(false);
                                        handleExcelExport();
                                    }}
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-xs font-bold text-white">XLS</span>
                                    <span>
                                        <span className="block">Excel workbook</span>
                                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">All rows in the current result list</span>
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-rose-50 dark:text-gray-100 dark:hover:bg-rose-900/20"
                                    onClick={() => {
                                        setExportMenuOpen(false);
                                        handlePdfExport();
                                    }}
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-xs font-bold text-white">PDF</span>
                                    <span>
                                        <span className="block">PDF report</span>
                                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">All rows in the current result list</span>
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>

                    <Button variant="secondary" onClick={() => setShowFilterModal(true)} className="flex items-center gap-2">
                        <Icon icon={FiFileText} className="h-4 w-4" />
                        Filters
                    </Button>

                    {hasActiveFilters && (
                        <Button variant="secondary" onClick={clearFilters}>
                            Clear Filters
                        </Button>
                    )}
                </div>
            </div>

            {/* Main grid table in premium layout */}
            <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden shadow-sm dark:border-slate-800 dark:bg-slate-900/40">
                <Table
                    data={filtered}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    onRowContextMenu={handleRowContextMenu}
                    draggable={false}
                />
            </div>

            {/* Custom Context Menu (Right Click Dropdown) */}
            {contextMenu.visible && contextMenu.survey && (
                <div
                    id="lease-renewal-surveys-row-context"
                    role="menu"
                    className="fixed z-[200] min-w-[11rem] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-600 dark:bg-gray-900"
                    style={{
                        left: Math.min(
                            contextMenu.x,
                            (typeof window !== 'undefined' ? window.innerWidth : 1200) - 180
                        ),
                        top: Math.min(
                            contextMenu.y,
                            (typeof window !== 'undefined' ? window.innerHeight : 800) - 8
                        ),
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="p-1 space-y-0.5">
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                setSelectedSurvey(contextMenu.survey);
                                setDetailsModalMode('view');
                                setShowDetailsModal(true);
                                closeContextMenu();
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                        >
                            <Icon icon={FiEye} className="h-4 w-4 shrink-0 opacity-70" />
                            View Details
                        </button>

                        {/* PSO submission button trigger */}
                        {contextMenu.survey.status === 'Assigned' && 
                         contextMenu.survey.assignedTo === auth.currentUser?.uid && (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setSelectedSurvey(contextMenu.survey);
                                    setDetailsModalMode('submission');
                                    setShowDetailsModal(true);
                                    closeContextMenu();
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-950/20"
                            >
                                <Icon icon={FiEdit2} className="h-4 w-4 shrink-0 opacity-80" />
                                Submission
                            </button>
                        )}

                        {contextMenu.survey.status === 'Submitted' && (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    if (contextMenu.survey) triggerSinglePdf(contextMenu.survey);
                                    closeContextMenu();
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/20"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4 shrink-0 opacity-80" />
                                Download PDF
                            </button>
                        )}

                        {isBankUser && (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    if (contextMenu.survey) setSurveyToDelete(contextMenu.survey);
                                    closeContextMenu();
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/20 border-t border-gray-100 dark:border-gray-800 mt-1 pt-1.5"
                            >
                                <Icon icon={FiTrash2} className="h-4 w-4 shrink-0 opacity-80" />
                                Delete Request
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Filter Modal */}
            <Modal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title="Filter Surveys"
                size="sm"
            >
                <div className="space-y-4">
                    {/* Status filter */}
                    <div>
                        <label className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-300 block">
                            Survey Status
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white py-2 px-3 text-xs outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        >
                            <option value="all">All Statuses</option>
                            <option value="Assigned">Assigned</option>
                            <option value="Submitted">Submitted</option>
                        </select>
                    </div>

                    {/* PSO filter */}
                    <div>
                        <label className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-300 block">
                            Assigned PSO Name
                        </label>
                        <select
                            value={psoFilter}
                            onChange={(e) => setPsoFilter(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white py-2 px-3 text-xs outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        >
                            <option value="all">All PSOs</option>
                            {psoNamesList.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Request date start */}
                    <div>
                        <label className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-300 block">
                            Request Date From
                        </label>
                        <input
                            type="date"
                            value={requestDateStart}
                            onChange={(e) => setRequestDateStart(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white py-2 px-3 text-xs outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        />
                    </div>

                    {/* Request date end */}
                    <div>
                        <label className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-300 block">
                            Request Date To
                        </label>
                        <input
                            type="date"
                            value={requestDateEnd}
                            onChange={(e) => setRequestDateEnd(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white py-2 px-3 text-xs outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        />
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
                        <Button
                            variant="secondary"
                            onClick={clearFilters}
                            className="flex-1"
                        >
                            Reset
                        </Button>
                        <Button
                            variant="primary"
                            onClick={applyFilters}
                            className="flex-1"
                        >
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Initiate Request Modal */}
            <AddLeaseRenewalSurveyModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={(msg) => showToast(msg || 'Lease renewal request initiated.', 'success')}
            />

            {/* Details / Submission Modal */}
            <LeaseRenewalSurveyDetailsModal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedSurvey(null);
                }}
                survey={selectedSurvey}
                mode={detailsModalMode}
                onSuccess={(msg) => showToast(msg || 'Lease survey processed.', 'success')}
            />

            {/* Bottom Left Export Progress Loader Widget (matches Branches export progress loader) */}
            {exportProgress.active && (
                <div className="pointer-events-auto fixed bottom-4 left-4 z-[100] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-md dark:border-slate-600 dark:bg-slate-900/95">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 dark:from-slate-900 dark:to-slate-800">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-white">
                                <Icon icon={FiDownload} className="h-4 w-4 opacity-90 animate-bounce" />
                                <span className="text-sm font-semibold tracking-tight">
                                    {exportProgress.format === 'excel' ? 'Exporting Excel Spreadsheet' : 'Exporting PDF Summary Report'}
                                </span>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-cyan-300">{Math.round(exportProgress.percent)}%</span>
                        </div>
                    </div>
                    <div className="space-y-3 p-4">
                        <div className="flex flex-col gap-1 text-xs text-slate-600 dark:text-slate-300">
                            <p className="font-semibold text-slate-800 dark:text-slate-200">{exportProgress.message}</p>
                            {exportProgress.totalRows > 0 && (
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Processed: <span className="font-semibold text-slate-700 dark:text-slate-300">{exportProgress.processedRows}</span> of <span className="font-semibold text-slate-700 dark:text-slate-300">{exportProgress.totalRows}</span> surveys
                                </p>
                            )}
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-[width] duration-300 ease-out"
                                style={{ width: `${Math.min(100, Math.max(2, exportProgress.percent))}%` }}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            {!exportProgress.done ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        cancelExportRef.current = true;
                                    }}
                                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition dark:border-red-900/30 dark:bg-red-950/20 dark:text-red-400"
                                >
                                    Cancel
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setExportProgress(p => ({ ...p, active: false }));
                                    }}
                                    className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                >
                                    Done
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Modal
                isOpen={!!surveyToDelete}
                onClose={() => setSurveyToDelete(null)}
                title="Confirm Delete Request"
                size="sm"
                footer={
                    <div className="flex justify-end gap-3 px-5 py-4 sm:px-6">
                        <Button
                            variant="secondary"
                            onClick={() => setSurveyToDelete(null)}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <button
                            onClick={handleDeleteSurvey}
                            disabled={loading}
                            className="inline-flex items-center justify-center rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500 disabled:opacity-50"
                        >
                            {loading ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                }
            >
                <div className="p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Are you sure you want to delete the lease renewal survey request{' '}
                        <strong className="text-gray-900 dark:text-white">
                            {surveyToDelete?.surveyId || 'N/A'}
                        </strong>?
                    </p>
                </div>
            </Modal>
        </div>
    );
}
